import { useMemo, useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import { useUser } from '@/shared/hooks/useAuth';
import type {
  Scope,
  DealItem,
  LeadItem,
  FollowupItem,
  ActivityItem,
  StopItem,
  SampleItem,
  QuotationItem,
  DispatchItem,
  UpcomingVisitItem,
  TaskItem,
  TrendPoint,
} from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Every value this hook returns is derived from a real query result. Nothing
// here is invented: a metric with no honest source renders as 0/empty rather
// than a plausible-looking placeholder — the same rule src/shared/dashboard/
// kpi.js applies server-side ("a tile with no honest source is NOT shipped as
// a zero or a placeholder — it is omitted").
// ─────────────────────────────────────────────────────────────────────────────

/** Known pipeline stage keys → display label. Labels only — never counts or
 *  values — so a stale label (an admin renamed a stage) is a cosmetic risk,
 *  not a data-integrity one. */
const STAGE_LABELS: Record<string, string> = {
  new_lead: 'New Lead',
  contacted: 'Contacted',
  sampling: 'Sample Trial',
  quotation_sent: 'Quotation Sent',
  negotiation: 'Negotiation',
  order_confirmed: 'Order Confirmed',
  dispatch: 'Dispatch',
  closed_won: 'Closed Won',
  closed_lost: 'Closed Lost',
};
const STAGE_ORDER = Object.keys(STAGE_LABELS);
const stageLabelOf = (stage: string) => STAGE_LABELS[stage] || stage;
/** A deterministic win-probability proxy from real stage progression — not an
 *  invented per-deal number, just "how far along the known ladder is this." */
const stageProbability = (stage: string) => {
  const idx = STAGE_ORDER.indexOf(stage);
  if (idx < 0) return 50;
  return Math.round(((idx + 1) / STAGE_ORDER.length) * 100);
};

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const startOfToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };
const startOfMonth = () => { const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0); return d; };

function formatShortDate(value?: string | Date | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatTime(value?: string | Date | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true });
}

/** Produces the literal "today"/"tomorrow"/"overdue" substrings FollowupCenter
 *  and MyTasksWidget match on for their tab filters — the contract those
 *  components' filtering logic already depends on. */
function dueDateLabel(value: string | Date | null | undefined, { overdue = false } = {}): string {
  if (overdue) return 'Overdue';
  if (!value) return 'Upcoming';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 'Upcoming';
  const now = new Date();
  const tomorrow = new Date(startOfToday()); tomorrow.setDate(tomorrow.getDate() + 1);
  if (isSameDay(d, now)) return 'Today';
  if (isSameDay(d, tomorrow)) return 'Tomorrow';
  return formatShortDate(d);
}

function relativeTime(value?: string | Date | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

/** Real free-text `source` values (LEAD_SOURCE_CHANNELS et al) collapsed onto
 *  the dashboard's own small display union — a label mapping, not new data. */
function mapLeadSource(raw: string): LeadItem['source'] {
  const s = (raw || '').toLowerCase();
  if (s.includes('web')) return 'Website';
  if (s.includes('visit') || s.includes('field')) return 'Field Visit';
  if (s.includes('refer')) return 'Referral';
  if (s.includes('campaign') || s.includes('ad')) return 'Campaign';
  if (s.includes('call') || s.includes('cold')) return 'Cold Call';
  return 'Incoming';
}

function mapLeadStatus(raw: string): LeadItem['status'] {
  const allowed: LeadItem['status'][] = ['new', 'contacted', 'qualified', 'sampling', 'quotation', 'negotiation'];
  if ((allowed as string[]).includes(raw)) return raw as LeadItem['status'];
  if (raw === 'interested') return 'qualified';
  if (raw === 'converted') return 'quotation';
  return 'new';
}

function mapVisitPurpose(purposeName: string): UpcomingVisitItem['purposeType'] {
  const p = (purposeName || '').toLowerCase();
  if (p.includes('demo')) return 'product_demo';
  if (p.includes('quotation') || p.includes('quote') || p.includes('price')) return 'quotation_discussion';
  if (p.includes('sample')) return 'sample_delivery';
  if (p.includes('initial') || p.includes('first')) return 'initial_meeting';
  return 'followup';
}

/** A currency's share, in that currency (e.g. USD 12,400 across 2 deals). */
export interface ForeignAmount { currency: string; count: number; value: number }

export function useSalesEmployeeData() {
  const user = useUser();
  const queryClient = useQueryClient();
  const [scope, setScope] = useState<Scope>('mine');

  // ── Real queries ─────────────────────────────────────────────────────────
  const kpiQuery = useQuery({
    queryKey: ['dashboard-kpi', scope],
    queryFn: async () => (await api.get(`/api/dashboard-kpi?scope=${scope}`)).data as {
      tiles: { key: string; count?: number; value?: number | null; available: boolean }[];
      trends?: { leads?: TrendPoint[]; quotations?: TrendPoint[] };
    },
  });

  const overdueFollowupsQuery = useQuery({
    queryKey: ['followups-overdue', scope],
    queryFn: async () => (await api.get(`/api/followups/overdue?scope=${scope}`)).data as { followups: any[]; count: number },
  });

  const dueTodayFollowupsQuery = useQuery({
    queryKey: ['followups-due-today', scope],
    queryFn: async () => (await api.get(`/api/followups/due-today?scope=${scope}`)).data as { followups: any[]; count: number },
  });

  const upcomingFollowupsQuery = useQuery({
    queryKey: ['followups-upcoming', scope],
    queryFn: async () => (await api.get(`/api/followups/upcoming?scope=${scope}`)).data as { followups: any[]; count: number },
  });

  const myStatsQuery = useQuery({
    queryKey: ['my-stats'],
    queryFn: async () => (await api.get('/api/my/stats')).data as { totalLeads: number; leadsToday: number; leadsWeek: number },
  });

  const dealsQuery = useQuery({
    queryKey: ['deals', 'mine', user?.id],
    // The old /api/deals router filters by the snake_case `assigned_to` query param,
    // not camelCase `assignedTo`. Using the wrong casing silently returned all
    // department deals instead of just this user's, or nothing at all.
    queryFn: async () => (await api.get(`/api/deals?assigned_to=${user?.id || ''}`)).data as { deals: any[] },
    enabled: !!user?.id,
  });

  const quotationsQuery = useQuery({
    queryKey: ['quotations', 'dashboard'],
    queryFn: async () => {
      const { data } = await api.get('/api/sales-ops/orders?documentType=quotation&limit=50&compact=true&sort=date_desc');
      return (data?.data ?? []) as any[];
    },
  });

  const samplesQuery = useQuery({
    queryKey: ['sampling', 'dashboard', 'mine'],
    queryFn: async () => {
      const { data } = await api.get('/api/sampling?mine=true&limit=50');
      return (data?.items ?? []) as any[];
    },
  });

  const visitsQuery = useQuery({
    queryKey: ['visits', 'dashboard', scope],
    queryFn: async () => {
      const { data } = await api.get(`/api/visits?scope=${scope}`);
      return (data?.visits ?? []) as any[];
    },
  });

  const tasksQuery = useQuery({
    queryKey: ['tasks', 'dashboard'],
    queryFn: async () => {
      const { data } = await api.get('/api/tasks');
      return (data?.tasks ?? []) as any[];
    },
  });

  const recentLeadsQuery = useQuery({
    queryKey: ['pipeline', 'recent-leads'],
    queryFn: async () => {
      const { data } = await api.get('/api/pipeline?side=lead&sort=date_desc&limit=5');
      return (data?.items ?? []) as any[];
    },
  });

  const wonThisMonthQuery = useQuery({
    queryKey: ['pipeline', 'won-month'],
    queryFn: async () => {
      const { data } = await api.get(
        `/api/pipeline?stage=closed_won&startDate=${startOfMonth().toISOString()}&limit=200`,
      );
      return data as { items: any[]; total: number };
    },
  });

  const targetAchievementQuery = useQuery({
    queryKey: ['targets', 'achievement', 'sales'],
    queryFn: async () => {
      const { data } = await api.get('/api/targets/achievement?type=sales');
      return data as { rows: { amount: { target: number; achieved: number; projectedClose: number | null } }[] };
    },
  });

  const profileQuery = useQuery({
    queryKey: ['workspace', 'profile', 'me'],
    queryFn: async () => (await api.get('/api/workspace/profile')).data?.profile as
      | { activity?: { id: string; action?: string; detail?: string; created_at: string }[] }
      | undefined,
  });

  const dispatchQueueQuery = useQuery({
    queryKey: ['dispatch-queue', 'dashboard', 'mine'],
    queryFn: async () => {
      // My own orders only, no samples (client, 2026-10-06).
      const { data } = await api.get('/api/sales-ops/orders/dispatch-queue?mine=1');
      return (data?.data ?? []) as any[];
    },
  });

  // ── Tiles from the one authoritative KPI source (CR14) ──────────────────
  const tile = (key: string) => kpiQuery.data?.tiles?.find((t) => t.key === key);
  const openLeadsTile = tile('openLeads');
  const openQuotationsTile = tile('openQuotations');
  const openProformaTile = tile('openProforma');
  const openSalesTile = tile('openSales');
  const totalContactsTile = tile('totalContacts');
  const openTasksTile = tile('openTasks');

  const kpiCounts = useMemo(() => ({
    openEnquiries: openLeadsTile?.count ?? 0,
    openQuotations: openQuotationsTile?.count ?? 0,
    openProforma: openProformaTile?.count ?? 0,
    openSales: openSalesTile?.count ?? 0,
    totalContacts: totalContactsTile?.count ?? 0,
    openTasks: openTasksTile?.count ?? 0,
    overdueFollowups: overdueFollowupsQuery.data?.count ?? 0,
    dueToday: dueTodayFollowupsQuery.data?.count ?? 0,
  }), [openLeadsTile, openQuotationsTile, openProformaTile, openSalesTile, totalContactsTile, openTasksTile,
    overdueFollowupsQuery.data, dueTodayFollowupsQuery.data]);

  // ── Today's visits (real, from the Visit Planner) ────────────────────────
  const todayVisitsRaw = useMemo(() => {
    const today = startOfToday();
    return (visitsQuery.data ?? []).filter((v) => {
      const d = new Date(v.plannedAt);
      return !Number.isNaN(d.getTime()) && isSameDay(d, today) && v.status !== 'cancelled';
    });
  }, [visitsQuery.data]);

  const heroData = useMemo(() => {
    // Made in this CRM, in rupees (server: pipeline) — the tiles also count
    // the historic quotations imported from the old system.
    const serverPipeline = (kpiQuery.data as { pipeline?: { count: number; value: number; foreign?: ForeignAmount[] } | null } | undefined)?.pipeline;
    const pipelineValue = serverPipeline ? serverPipeline.value : (openQuotationsTile?.value || 0) + (openProformaTile?.value || 0) + (openSalesTile?.value || 0);
    const openDealsCount = serverPipeline ? serverPipeline.count : (openQuotationsTile?.count || 0) + (openProformaTile?.count || 0) + (openSalesTile?.count || 0);
    const wonItems = wonThisMonthQuery.data?.items ?? [];
    // Sales this month = tax invoices raised this month (server: myMonth).
    const myMonth = (kpiQuery.data as { myMonth?: { invoices: number; value: number; collected: number; foreign?: ForeignAmount[] } | null } | undefined)?.myMonth;
    return {
      pipelineValue,
      openDealsCount,
      // Pipeline records store deal value in `grandTotal`, not `value`.
      // Summing a non-existent field always produced 0.
      wonMonthValue: myMonth ? myMonth.value : wonItems.reduce((s, r) => s + (Number(r.grandTotal) || 0), 0),
      wonDealsCount: myMonth ? myMonth.invoices : (wonThisMonthQuery.data?.total ?? wonItems.length),
      collectedMonth: myMonth ? myMonth.collected : null,
      // USD / other-currency business, counted separately from the rupee figures.
      pipelineForeign: serverPipeline?.foreign ?? [],
      monthForeign: myMonth?.foreign ?? [],
      totalLeads: myStatsQuery.data?.totalLeads ?? 0,
      leadsWeek: myStatsQuery.data?.leadsWeek ?? 0,
      overdueCount: overdueFollowupsQuery.data?.count ?? 0,
      visitsCount: todayVisitsRaw.length,
    };
  }, [openQuotationsTile, openProformaTile, openSalesTile, wonThisMonthQuery.data, kpiQuery.data, myStatsQuery.data,
    overdueFollowupsQuery.data, todayVisitsRaw]);

  // ── Priority alerts — every count real or defensibly derived from real dates ─
  const alertData = useMemo(() => {
    const quotes = quotationsQuery.data ?? [];
    const OPEN_QUOTE_STATUSES = ['draft', 'quotation'];
    const expiringQuotesCount = quotes.filter((q) => {
      if (!OPEN_QUOTE_STATUSES.includes(q.status)) return false;
      const ageDays = (Date.now() - new Date(q.date).getTime()) / 86400000;
      return ageDays >= 7; // Same "ageing open quote" window used for feedback SLAs elsewhere in this app.
    }).length;
    const samples = samplesQuery.data ?? [];
    const sampleFeedbackCount = samples.filter((s) => ['feedback_received', 'technical_review'].includes(s.status)).length;
    const overdueDispatchesCount = (dispatchQueueQuery.data ?? []).filter((o: any) => o.isOverdue).length;
    return {
      overdueCount: overdueFollowupsQuery.data?.count ?? 0,
      dueTodayCount: dueTodayFollowupsQuery.data?.count ?? 0,
      // "Hot" = genuinely fresh today, not an invented urgency score.
      hotLeadsCount: myStatsQuery.data?.leadsToday ?? 0,
      expiringQuotesCount,
      sampleFeedbackCount,
      overdueDispatchesCount,
    };
  }, [overdueFollowupsQuery.data, dueTodayFollowupsQuery.data, myStatsQuery.data, quotationsQuery.data, samplesQuery.data, dispatchQueueQuery.data]);

  // ── Sales target (real Target/Achievement engine) ────────────────────────
  const targetRow = targetAchievementQuery.data?.rows?.[0];
  const targetData = useMemo(() => ({
    target: targetRow?.amount?.target ?? 0,
    achieved: targetRow?.amount?.achieved ?? 0,
    expectedClosing: targetRow?.amount?.projectedClose ?? 0,
    hasTarget: !!targetRow && targetRow.amount.target > 0,
  }), [targetRow]);

  // ── Priority deals (real, from /api/deals) ───────────────────────────────
  const priorityDeals: DealItem[] = useMemo(() => {
    const rows = dealsQuery.data?.deals ?? [];
    return rows
      .filter((d) => d.stage !== 'closed_won' && d.stage !== 'closed_lost')
      // Deal value lives in `grandTotal` (pipeline schema), not `value`.
      .sort((a, b) => (Number(b.grandTotal) || 0) - (Number(a.grandTotal) || 0))
      .slice(0, 6)
      .map((d) => ({
        id: String(d.id || d._id),
        // Lead/deal schema: `business_name` is the company, `name` or `title` is the contact.
        name: d.business_name || d.title || d.name || '',
        client: d.contact_name || d.name || '',
        stage: (STAGE_ORDER.includes(d.stage) ? d.stage : 'new_lead') as DealItem['stage'],
        stageLabel: stageLabelOf(d.stage),
        // grandTotal is the real value field on the merged pipeline schema.
        value: Number(d.grandTotal) || 0,
        probability: stageProbability(d.stage),
        lastActivity: d.notes ? String(d.notes).slice(0, 90) : `Stage: ${stageLabelOf(d.stage)}`,
        // Schema fields are `follow_up_date` and `follow_up_time` (snake_case).
        nextFollowup: d.follow_up_date
          ? `${d.follow_up_date}${d.follow_up_time ? ` ${d.follow_up_time}` : ''}`
          : 'No follow-up scheduled',
        phone: d.phone || '',
        // `emails` on the schema is an array; fall back to `email` string if present.
        email: Array.isArray(d.emails) ? (d.emails[0] || '') : (d.email || ''),
        city: d.location || d.address || '',
      }));
  }, [dealsQuery.data]);

  // ── Recent leads (real, from the unified pipeline) ───────────────────────
  const recentLeads: LeadItem[] = useMemo(() => {
    return (recentLeadsQuery.data ?? []).map((r) => ({
      id: String(r.id || r._id),
      name: r.name,
      company: r.business_name || '',
      phone: r.phone || '',
      email: Array.isArray(r.emails) ? r.emails[0] || '' : '',
      source: mapLeadSource(r.source),
      createdDate: formatShortDate(r.created_at),
      status: mapLeadStatus(r.status),
      statusLabel: r.status ? r.status.charAt(0).toUpperCase() + r.status.slice(1) : 'New',
      notes: r.notes || '',
    }));
  }, [recentLeadsQuery.data]);

  // ── Follow-ups (real, merged overdue + due-today + upcoming) ─────────────
  const [completedFollowupIds, setCompletedFollowupIds] = useState<Set<string>>(new Set());
  const followupsBase: FollowupItem[] = useMemo(() => {
    const relatedTypeOf = (parentType: string): FollowupItem['relatedType'] =>
      (['lead', 'deal', 'quotation', 'proforma', 'invoice', 'sample'] as string[]).includes(parentType) ? (parentType as FollowupItem['relatedType']) : 'lead';
    const mapRow = (f: any, overdue: boolean): FollowupItem => {
      const summary = f.parentSummary || {};
      const phone = f.contactPhone || f.contactPersonPhone || summary.phone || '';
      const label = summary.label || (f.parentType ? f.parentType.charAt(0).toUpperCase() + f.parentType.slice(1) : 'Record');
      const number = summary.number ? ` · ${summary.number}` : '';
      return {
        id: String(f._id || f.id),
        parentId: String(f.parentId || ''),
        contactName: summary.contactName || phone || 'No contact on file',
        company: summary.company || `${label} follow-up`,
        phone,
        email: f.contactEmail || f.contactPersonEmail || summary.email || '',
        type: (['call', 'whatsapp', 'email', 'visit', 'meeting'] as string[]).includes(f.contactMethod) ? f.contactMethod : 'none',
        dueTime: formatTime(f.followupDate),
        dueDate: dueDateLabel(f.followupDate, { overdue }),
        priority: overdue ? 'high' : 'medium',
        relatedEntity: `${label}${number} follow-up`,
        relatedType: relatedTypeOf(f.parentType),
        lastInteraction: f.customerResponse || undefined,
        note: f.followupNote || '',
        isOverdue: overdue,
      };
    };
    const overdueRows = (overdueFollowupsQuery.data?.followups ?? []).map((f) => mapRow(f, true));
    const dueTodayRows = (dueTodayFollowupsQuery.data?.followups ?? []).map((f) => mapRow(f, false));
    const upcomingRows = (upcomingFollowupsQuery.data?.followups ?? []).map((f) => mapRow(f, false));

    const seenIds = new Set<string>();
    const merged: FollowupItem[] = [];
    for (const row of [...overdueRows, ...dueTodayRows, ...upcomingRows]) {
      if (!seenIds.has(row.id)) {
        seenIds.add(row.id);
        merged.push(row);
      }
    }
    return merged;
  }, [overdueFollowupsQuery.data, dueTodayFollowupsQuery.data, upcomingFollowupsQuery.data]);

  const followups = useMemo(
    () => followupsBase.map((f) => (completedFollowupIds.has(f.id) ? { ...f, isOverdue: false, dueDate: 'Completed' } : f)),
    [followupsBase, completedFollowupIds],
  );
  const setFollowups = () => { /* completion is tracked via handleCompleteFollowup below */ };
  const markFollowupComplete = useCallback(async (id: string) => {
    setCompletedFollowupIds((prev) => new Set(prev).add(id));
    try {
      await api.patch(`/api/followups/${id}`, { status: 'done' });
      queryClient.invalidateQueries({ queryKey: ['followups-overdue'] });
      queryClient.invalidateQueries({ queryKey: ['followups-due-today'] });
      queryClient.invalidateQueries({ queryKey: ['followups-upcoming'] });
      queryClient.invalidateQueries({ queryKey: ['followups'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-kpi'] });
    } catch (err) {
      console.error('Failed to update followup status:', err);
    }
  }, [queryClient]);

  // ── Tasks (real, from /api/tasks) ────────────────────────────────────────
  const DONE_STATUSES = ['done', 'completed', 'closed', 'cancelled', 'archived'];
  const [tasks, setTasksState] = useState<TaskItem[]>([]);
  const tasksFromServer: TaskItem[] = useMemo(() => {
    const today = startOfToday();
    return (tasksQuery.data ?? []).map((t) => {
      const due = t.due_date ? new Date(t.due_date) : null;
      const isCompleted = DONE_STATUSES.includes(t.status);
      let dueCategory: TaskItem['dueCategory'] = 'upcoming';
      if (due && !Number.isNaN(due.getTime())) {
        if (isSameDay(due, today)) dueCategory = 'today';
        else if (due < today) dueCategory = 'overdue';
      }
      return {
        id: String(t._id || t.id),
        title: t.title,
        customer: t.client_name || '',
        priority: (['high', 'medium', 'low'] as string[]).includes(t.priority) ? t.priority : 'medium',
        dueDate: t.due_date ? formatShortDate(t.due_date) : 'No due date',
        dueCategory,
        isCompleted,
        notes: t.description || '',
      } as TaskItem;
    });
  }, [tasksQuery.data]);
  // Local toggle overlays the server snapshot so a click responds instantly;
  // then api.patch persists the change to the database.
  const tasksView = tasks.length ? tasks : tasksFromServer;
  const toggleTask = useCallback(async (taskId: string) => {
    const current = tasksView.find((t) => t.id === taskId);
    const nextCompleted = !current?.isCompleted;
    const nextStatus = nextCompleted ? 'done' : 'todo';
    setTasksState((prev) => {
      const base = prev.length ? prev : tasksFromServer;
      return base.map((t) => (t.id === taskId ? { ...t, isCompleted: nextCompleted } : t));
    });
    try {
      await api.patch(`/api/tasks/${taskId}`, { status: nextStatus });
      queryClient.invalidateQueries({ queryKey: ['tasks', 'dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-kpi'] });
    } catch (err) {
      console.error('Failed to toggle task status:', err);
    }
  }, [tasksView, tasksFromServer, queryClient]);

  // ── Samples (real, from /api/sampling) ───────────────────────────────────
  const samples: SampleItem[] = useMemo(() => {
    const mapStatus = (s: string): SampleItem['status'] => {
      if (s === 'draft') return 'pending_dispatch';
      if (s === 'dispatched' || s === 'delivered') return 'sent';
      if (s === 'feedback_received' || s === 'technical_review') return 'awaiting_feedback';
      if (s === 'converted') return 'converted';
      return 'successful';
    };
    return (samplesQuery.data ?? []).map((s) => {
      const dispatchedAt = s.dispatch?.dispatchedAt || s.createdAt;
      const feedbackDue = dispatchedAt ? new Date(dispatchedAt) : null;
      if (feedbackDue) feedbackDue.setDate(feedbackDue.getDate() + 7); // same window sampling.awaitingFeedback() uses
      const lines = Array.isArray(s.lines) ? s.lines : [];
      return {
        id: String(s._id || s.id),
        client: s.customerName || '',
        product: lines.length ? `${lines[0].productName}${lines.length > 1 ? ` +${lines.length - 1} more` : ''}` : 'Sample',
        status: mapStatus(s.status),
        statusLabel: (s.status || '').replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()) || 'Draft',
        dispatchDate: formatShortDate(dispatchedAt),
        feedbackDueDate: feedbackDue ? formatShortDate(feedbackDue) : '',
        contactPerson: s.customerName || '',
        phone: s.phone || '',
      } as SampleItem;
    });
  }, [samplesQuery.data]);

  // ── Quotations (real, from sales-ops orders) ─────────────────────────────
  const quotations: QuotationItem[] = useMemo(() => {
    const OPEN = ['draft', 'quotation'];
    return (quotationsQuery.data ?? []).slice(0, 8).map((q) => {
      const ageDays = Math.max(0, Math.floor((Date.now() - new Date(q.date).getTime()) / 86400000));
      const isOpen = OPEN.includes(q.status);
      const isExpiring = isOpen && ageDays >= 7;
      const status: QuotationItem['status'] = q.status === 'confirmed'
        ? 'converted'
        : isExpiring ? 'expiring_soon' : isOpen ? (ageDays > 0 ? 'awaiting_response' : 'sent') : 'draft';
      const statusLabel = status === 'converted' ? 'Converted to order'
        : status === 'expiring_soon' ? `Sent ${ageDays} days ago`
          : status === 'awaiting_response' ? `Sent ${ageDays} day${ageDays === 1 ? '' : 's'} ago`
            : 'Sent today';
      const nextAction = status === 'converted' ? 'Dispatched'
        : status === 'expiring_soon' ? 'Urgent follow-up'
          : 'Follow-up';
      return {
        id: String(q._id),
        quoteNumber: q.orderNumber || q.documentNumber || '',
        client: q.customerName || '',
        amount: Number(q.grandTotal) || 0,
        date: formatShortDate(q.date),
        ageDays,
        status,
        statusLabel,
        nextAction,
        hasPdf: true,
      } as QuotationItem;
    });
  }, [quotationsQuery.data]);

  // ── Pending dispatch (real, from the dispatch queue) ─────────────────────
  const pendingDispatches: DispatchItem[] = useMemo(() => {
    // Still waiting to leave — already dispatched / delivered ones are done.
    return (dispatchQueueQuery.data ?? [])
      .filter((o) => o.documentType !== 'sample_request' && !['dispatched', 'delivered'].includes(o.stage))
      .slice(0, 8).map((o) => {
      const status: DispatchItem['status'] = o.stockStatus === 'ready' ? 'ready' : o.stockStatus === 'short' ? 'in_process' : 'packed';
      return {
        id: String(o._id),
        orderNumber: o.orderNumber || '',
        client: o.customerName || '',
        amount: Number(o.grandTotal) || 0,
        createdDate: formatShortDate(o.date),
        status,
        statusLabel: status === 'ready' ? 'Ready' : status === 'in_process' ? 'Stock Short' : 'Packing',
        shippingCity: o.shippingDestination || '',
        itemsCount: Number(o.lineCount) || 0,
        requiredDispatchDate: o.requiredDispatchDate || null,
        dueFormatted: formatShortDate(o.requiredDispatchDate),
        isOverdue: Boolean(o.isOverdue),
        overdueDays: Number(o.overdueDays) || 0,
      } as DispatchItem;
    });
  }, [dispatchQueueQuery.data]);

  // ── Upcoming visits (real, from the Visit Planner) ───────────────────────
  const upcomingVisits: UpcomingVisitItem[] = useMemo(() => {
    const now = new Date();
    return (visitsQuery.data ?? [])
      .filter((v) => v.status !== 'completed' && v.status !== 'cancelled' && new Date(v.plannedAt) >= startOfToday())
      .sort((a, b) => new Date(a.plannedAt).getTime() - new Date(b.plannedAt).getTime())
      .slice(0, 6)
      .map((v) => {
        const d = new Date(v.plannedAt);
        const day = isSameDay(d, now) ? 'Today' : isSameDay(d, new Date(now.getTime() + 86400000)) ? 'Tomorrow' : formatShortDate(d);
        return {
          id: String(v.id || v._id),
          dateTime: `${day}, ${formatTime(d)}`,
          client: v.contactSnapshot?.name || v.contactSnapshot?.company || 'Client',
          location: v.location || '',
          purpose: v.purposeName || 'Visit',
          purposeType: mapVisitPurpose(v.purposeName || ''),
          status: v.status === 'checked_in' ? 'confirmed' : v.status === 'planned' ? 'confirmed' : 'pending',
          contactPerson: v.contactSnapshot?.contactPersonName || v.contactSnapshot?.name || '',
          phone: v.contactSnapshot?.contactPersonPhone || v.contactSnapshot?.phone || '',
        } as UpcomingVisitItem;
      });
  }, [visitsQuery.data]);

  // ── Today's stops — only visits with real geocoded coordinates ───────────
  const todayStops: StopItem[] = useMemo(() => {
    return todayVisitsRaw
      .filter((v) => v.coordinates && typeof v.coordinates.lat === 'number' && typeof v.coordinates.lng === 'number')
      .sort((a, b) => new Date(a.plannedAt).getTime() - new Date(b.plannedAt).getTime())
      .map((v) => ({
        id: String(v.id || v._id),
        time: formatTime(v.plannedAt),
        client: v.contactSnapshot?.name || v.contactSnapshot?.company || 'Client',
        location: v.location || '',
        purpose: v.purposeName || 'Visit',
        status: v.status === 'completed' ? 'completed' : v.status === 'checked_in' ? 'in_progress' : 'planned',
        lat: v.coordinates.lat,
        lng: v.coordinates.lng,
      } as StopItem));
  }, [todayVisitsRaw]);

  // ── Today's activity timeline — merged real, timestamped events ─────────
  const todayActivities: ActivityItem[] = useMemo(() => {
    const today = startOfToday();
    // Sorted by the real instant, not the display string — '10:30 am' sorts
    // before '9:00 am' as text.
    const items: (ActivityItem & { at: number })[] = [];
    const at = (v?: string | Date | null) => (v ? new Date(v).getTime() || 0 : 0);
    for (const v of todayVisitsRaw) {
      const clientName = v.contactSnapshot?.name || v.contactSnapshot?.company || 'Client';
      if (v.checkOutAt) {
        items.push({ id: `visit-out-${v.id || v._id}`, time: formatTime(v.checkOutAt), title: 'Visit Completed', client: clientName, type: 'visit', status: 'completed', description: v.outcome || v.purposeName || '', at: at(v.checkOutAt) });
      } else if (v.checkInAt) {
        items.push({ id: `visit-in-${v.id || v._id}`, time: formatTime(v.checkInAt), title: 'Checked In at Client Site', client: clientName, type: 'visit', status: 'in_progress', description: v.purposeName || '', at: at(v.checkInAt) });
      } else {
        items.push({ id: `visit-${v.id || v._id}`, time: formatTime(v.plannedAt), title: 'Visit Planned', client: clientName, type: 'visit', status: 'upcoming', description: v.purposeName || '', at: at(v.plannedAt) });
      }
    }
    for (const s of (samplesQuery.data ?? [])) {
      const dispatchedAt = s.dispatch?.dispatchedAt ? new Date(s.dispatch.dispatchedAt) : null;
      if (dispatchedAt && isSameDay(dispatchedAt, today)) {
        items.push({ id: `sample-${s._id}`, time: formatTime(dispatchedAt), title: 'Sample Dispatched', client: s.customerName || '', type: 'sample', status: 'completed', description: s.lines?.[0]?.productName || '', at: dispatchedAt.getTime() });
      }
    }
    for (const q of (quotationsQuery.data ?? [])) {
      // `date` is the document date — a calendar day stored as UTC midnight,
      // which reads as 5:30 am in India. When it was made is createdAt.
      const createdAt = new Date(q.createdAt || q.date);
      if (isSameDay(createdAt, today)) {
        items.push({ id: `quote-${q._id}`, time: formatTime(createdAt), title: 'Quotation Created', client: q.customerName || '', type: 'quotation', status: 'completed', description: `${q.orderNumber || ''}`, at: createdAt.getTime() });
      }
    }
    return items.sort((a, b) => a.at - b.at).map(({ at: _at, ...item }) => item);
  }, [todayVisitsRaw, samplesQuery.data, quotationsQuery.data]);

  // ── Recent CRM activity — real audit log (ActivityLog via /workspace/profile) ─
  const crmActivities = useMemo(() => {
    return (profileQuery.data?.activity ?? []).slice(0, 12).map((a) => ({
      id: a.id,
      text: a.detail || a.action || 'Activity',
      time: relativeTime(a.created_at),
      type: a.action || 'note',
    }));
  }, [profileQuery.data]);

  // ── Trends — the same 10-day buckets /api/dashboard-kpi already computes ─
  const enquiryTrend: TrendPoint[] = kpiQuery.data?.trends?.leads ?? [];
  const quotationTrend: TrendPoint[] = kpiQuery.data?.trends?.quotations ?? [];

  const refetchAll = useCallback(async () => {
    await Promise.allSettled([
      kpiQuery.refetch(),
      overdueFollowupsQuery.refetch(),
      dueTodayFollowupsQuery.refetch(),
      myStatsQuery.refetch(),
      dealsQuery.refetch(),
      quotationsQuery.refetch(),
      samplesQuery.refetch(),
      visitsQuery.refetch(),
      tasksQuery.refetch(),
      recentLeadsQuery.refetch(),
      wonThisMonthQuery.refetch(),
      targetAchievementQuery.refetch(),
      profileQuery.refetch(),
      dispatchQueueQuery.refetch(),
    ]);
  }, [
    kpiQuery,
    overdueFollowupsQuery,
    dueTodayFollowupsQuery,
    myStatsQuery,
    dealsQuery,
    quotationsQuery,
    samplesQuery,
    visitsQuery,
    tasksQuery,
    recentLeadsQuery,
    wonThisMonthQuery,
    targetAchievementQuery,
    profileQuery,
    dispatchQueueQuery,
  ]);

  return {
    scope,
    setScope,
    user,
    // Real headline metrics
    heroData,
    kpiCounts,
    alertData,
    targetData,
    // Live queries (exposed for loading/error states where a page wants them)
    kpiQuery,
    overdueFollowupsQuery,
    dueTodayFollowupsQuery,
    myStatsQuery,
    dealsQuery,
    quotationsQuery,
    samplesQuery,
    visitsQuery,
    tasksQuery,
    // Prepared collections — every one derived above, none invented
    priorityDeals,
    recentLeads,
    followups,
    setFollowups,
    markFollowupComplete,
    tasks: tasksView,
    toggleTask,
    todayActivities,
    todayStops,
    samples,
    quotations,
    pendingDispatches,
    upcomingVisits,
    enquiryTrend,
    quotationTrend,
    crmActivities,
    refetchAll,
  };
}
